import { ColorInput, ColorRgba } from "types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { defaultFillColor, defaultLineColor, iconColorOptions, presetPolicyColors } from "../policies.const";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColorPicker } from "components";
import { IconColor } from "@/components/icon-color";
import { RgbaColor } from "react-colorful";
import { formatArrayToRgbaColor } from "@/helper/utils";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconColor as EIconColor } from "types/gql/graphql";

export interface ColorInputModalProps {
  currentColorInput: ColorInput;
  onDismiss: () => void;
  onApply: (value: ColorInput) => void;
}

const zNum = z.number().int().min(0).max(255);

export const ColorRgbaSchema = z.union([z.tuple([zNum, zNum, zNum]), z.tuple([zNum, zNum, zNum, zNum])]);

export const colorInputSchema = z.object({
  lineColor: ColorRgbaSchema.optional(),
  fillColor: ColorRgbaSchema.optional(),
  dashed: z.boolean().optional(),
  iconColor: z.enum(EIconColor).optional(),
});

export const ColorInputModal = ({ currentColorInput, onApply, onDismiss }: ColorInputModalProps) => {
  const form = useForm<z.infer<typeof colorInputSchema>>({
    resolver: zodResolver(colorInputSchema),
    defaultValues: {
      fillColor: currentColorInput.fillColor ?? defaultFillColor,
      lineColor: currentColorInput.lineColor ?? defaultLineColor,
      dashed: currentColorInput.dashed,
      iconColor: currentColorInput.iconColor,
    },
    mode: "all",
  });

  const handleChangeColor = (value: RgbaColor, type: "fillColor" | "lineColor") => {
    const formatColor: ColorRgba = [value.r, value.g, value.b, value.a !== undefined ? Math.ceil(value.a * 255) : 1];
    form.setValue(type, formatColor);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit((policyColor) => {
      onApply(policyColor);
      onDismiss();
    })(e);
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Color input</DialogTitle>
          <DialogDescription>Polygon color</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-between">
              <FormField
                control={form.control}
                name="fillColor"
                render={({ field: { value } }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <label
                          htmlFor="dash-border"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 w-25"
                        >
                          Fill Color
                        </label>
                        <ColorPicker
                          popoverClassName="-top-full left-full ml-3"
                          color={formatArrayToRgbaColor(value)}
                          presetColors={presetPolicyColors.fillColors}
                          onChange={(value) => handleChangeColor(value, "fillColor")}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="iconColor"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 w-25">
                          Couleur de l'icône
                        </label>
                        <IconColor
                          value={value}
                          popoverClassName="-top-full right-full"
                          presetColors={iconColorOptions}
                          onChange={(value) => onChange(value)}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="lineColor"
              render={({ field: { value } }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <label
                        htmlFor="dash-border"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 w-25"
                      >
                        Border Color
                      </label>
                      <ColorPicker
                        popoverClassName="-top-full left-full ml-3"
                        color={formatArrayToRgbaColor(value)}
                        presetColors={presetPolicyColors.lineColors}
                        onChange={(value) => handleChangeColor(value, "lineColor")}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dashed"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <label
                        htmlFor="dash-border"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 w-25"
                      >
                        Dashed border
                      </label>
                      <Checkbox checked={value} onCheckedChange={onChange} id="dash-border" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button onClick={onDismiss} className="mr-2" variant="secondary" type="button">
                Cancel
              </Button>
              <Button>Apply</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
